import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { fetchEpisodes } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import EpisodeCard from "@/components/EpisodeCard";

export default function Episodes() {
    const [episodes, setEpisodes] = useState([]);
    const [query, setQuery] = useState("");
    const [tag, setTag] = useState("all");

    useEffect(() => {
        fetchEpisodes().then(setEpisodes).catch(() => {});
    }, []);

    const tags = useMemo(
        () => ["all", ...new Set(episodes.flatMap((e) => e.tags))],
        [episodes]
    );

    const filtered = episodes.filter((e) => {
        const q = query.toLowerCase();
        const matchQ =
            !q ||
            e.title.toLowerCase().includes(q) ||
            e.guest.toLowerCase().includes(q) ||
            e.subtitle.toLowerCase().includes(q);
        const matchT = tag === "all" || e.tags.includes(tag);
        return matchQ && matchT;
    });

    return (
        <div data-testid="episodes-page">
            <section className="bg-paper border-b-2 border-ink">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-20">
                    <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                        The archive
                    </p>
                    <h1
                        data-testid="episodes-heading"
                        className="mt-5 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-ink"
                    >
                        <MaskedLine delay={0.1}>Every episode,</MaskedLine>
                        <MaskedLine delay={0.25} className="italic text-pink">
                            on the record.
                        </MaskedLine>
                    </h1>

                    <Reveal delay={0.35}>
                        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-5">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/50" />
                                <input
                                    data-testid="episodes-search-input"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search by title, guest, theme…"
                                    className="w-full bg-cream border-2 border-ink pl-11 pr-4 py-3 text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-pink"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by tag">
                                {tags.map((t) => (
                                    <button
                                        key={t}
                                        data-testid={`tag-chip-${t}`}
                                        onClick={() => setTag(t)}
                                        className={`font-mono-brand text-xs uppercase tracking-wider px-4 py-2.5 border-2 border-ink transition-colors duration-150 ${
                                            tag === t
                                                ? "bg-ink text-paper"
                                                : "bg-cream text-ink hover:bg-mustard/30"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
                {filtered.length === 0 ? (
                    <p
                        data-testid="episodes-empty-state"
                        className="font-sub font-bold text-xl text-ink/60 py-16"
                    >
                        Nothing in the archive matches that. Try another word.
                    </p>
                ) : (
                    <div
                        data-testid="episodes-grid"
                        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10"
                    >
                        {filtered.map((ep, i) => (
                            <EpisodeCard key={ep.id} ep={ep} index={i} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
